/**
 * The tenant's SSO connection — ONE declaration of the noun.
 *
 * Foundations 2 (Users rail), B2d-T4. decisions.md 2026-09-08 (Users rail):
 * *"Tenant self-service SSO does not exist today (the tenant page is a
 * placeholder pointing at the platform administrator) and is a required
 * Users-rail feature, to be built properly."*
 *
 * ZOD IS THE TYPE SOURCE (CLAUDE.md). The protocol alphabet, the write shape,
 * the read shape and the test-result vocabulary are declared here once:
 * `api/src/repositories/sso-connection.repository.ts` imports them rather than
 * re-declaring unions, the c36 CHECK constraints carry the same values, and
 * `packages/web/src/routes/users/sso.tsx` renders from the same types.
 *
 * ─── THE SECRET IS NEVER IN A SHAPE THAT LEAVES THE SERVER ────────────────
 *
 * `SsoConnectionWriteSchema` has a `clientSecret` — it is the ONLY shape that
 * does, it is inbound only, and what the store holds is the AES-256-GCM
 * ciphertext `middleware/field-encryption.ts` produces. `SsoConnectionSchema`
 * — the read shape, the one every response uses — has no secret field at all
 * and carries `hasClientSecret: boolean` instead, so "is a secret configured"
 * is answerable without the secret being answerable. A field that does not
 * exist cannot be leaked by a serializer that forgot to strip it. This is the
 * same rule `email-settings.ts` states for the SMTP password, and it is the
 * rule the surface this task DELETES got wrong: `admin/security/auth.ts` kept
 * the OIDC client secret and the SAML private key inside an opaque `metadata`
 * jsonb and had to strip them on the way out with a key-name denylist
 * (`redactSsoMetadata`), which is a guess about what a passthrough blob
 * contains rather than a shape that cannot carry it.
 *
 * ─── A FAILED TEST STILL SAVES ────────────────────────────────────────────
 *
 * decisions.md 2026-09-13 #16: a provider that fails its test connection is
 * still SAVED, with the failure and its timestamp recorded on the connection,
 * and the screen shows the last test result plainly so a never-successful
 * connection cannot look healthy. `lastTest` below is that record, and it is
 * `null` — not "success" — until a test has actually run.
 */

import { z } from 'zod';

// ─── Vocabularies ──────────────────────────────────────────────────────────

/**
 * What a tenant admin may configure on this surface: an OIDC provider or a
 * SAML 2.0 provider.
 *
 * `google` and `entra` are NOT here, and the store's CHECK admits them only
 * because rows written before this task may still carry them (the deleted
 * Admin › Security › Auth form offered all four). They are OIDC providers with
 * a vendor name on the front; a tenant configures either of them by pointing an
 * `oidc` connection at the vendor's issuer, which is one code path instead of
 * three. `SsoStoredProtocolSchema` is what the READ shape accepts, so a legacy
 * row still renders.
 */
export const SsoProtocolSchema = z.enum(['oidc', 'saml']);
export type SsoProtocol = z.infer<typeof SsoProtocolSchema>;

/** Every protocol value the store may hold, legacy rows included. */
export const SsoStoredProtocolSchema = z.enum([
  'oidc',
  'saml',
  'google',
  'entra',
]);
export type SsoStoredProtocol = z.infer<typeof SsoStoredProtocolSchema>;

/**
 * The outcome of a test connection. There are exactly two, and the absence of
 * a row is the third state the UI must render differently — "never tested" is
 * not "passing".
 */
export const SsoTestOutcomeSchema = z.enum(['success', 'failure']);
export type SsoTestOutcome = z.infer<typeof SsoTestOutcomeSchema>;

// ─── The department scope a JIT-provisioned account starts with ────────────

/**
 * decisions.md 2026-09-08: *"A user belongs to one or more departments, or to
 * all."* The connection carries the DEFAULT for the accounts it provisions, in
 * exactly the two-part shape `repositories/user-department.repository.ts`
 * writes: every department, or a named set.
 */
export const SsoDepartmentScopeSchema = z
  .object({
    all: z.boolean(),
    ids: z.array(z.string().uuid()).max(200).default([]),
  })
  .superRefine((value, ctx) => {
    if (!value.all && value.ids.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ids'],
        message:
          'Choose at least one department, or scope provisioned users to all departments.',
      });
    }
    if (value.all && value.ids.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ids'],
        message:
          'A connection scoped to all departments cannot also name departments.',
      });
    }
  });
export type SsoDepartmentScope = z.infer<typeof SsoDepartmentScopeSchema>;

/** decisions.md C6 — *"default Member, all departments"*. */
export const SSO_DEFAULT_ROLE_KEY = 'member';
export function defaultSsoDepartmentScope(): SsoDepartmentScope {
  return { all: true, ids: [] };
}

// ─── The connection ────────────────────────────────────────────────────────

const nameField = z.string().trim().min(1).max(120);
const urlField = z
  .string()
  .trim()
  .url()
  .max(2048)
  .refine((v) => v.startsWith('https://') || v.startsWith('http://'), {
    message: 'Must be an http(s) URL.',
  });

/**
 * The OIDC half. `issuer` is the identity of the provider and the thing
 * discovery is performed against; everything else on the wire (authorization
 * endpoint, token endpoint, JWKS) is READ FROM the provider rather than typed
 * in twice, which is why there are no endpoint fields here.
 */
export const SsoOidcConfigSchema = z.object({
  issuer: urlField,
  clientId: z.string().trim().min(1).max(255),
  /** Space-free scope keys; `openid` is added by the server and need not be typed. */
  scopes: z.array(z.string().trim().min(1).max(64)).max(20).default([]),
});
export type SsoOidcConfig = z.infer<typeof SsoOidcConfigSchema>;

/**
 * The SAML half. `certificate` is the IdP's signing certificate — PUBLIC
 * material, which is why it is a plain column and not a secret: it verifies
 * the assertion, it does not sign anything on our side.
 */
export const SsoSamlConfigSchema = z.object({
  entityId: z.string().trim().min(1).max(1024),
  ssoUrl: urlField,
  certificate: z.string().trim().min(1).max(20000),
  /** Where the IdP's metadata can be re-fetched; optional, used by the test. */
  metadataUrl: urlField.optional().nullable(),
});
export type SsoSamlConfig = z.infer<typeof SsoSamlConfigSchema>;

/**
 * What the tenant admin sends. Discriminated on `protocol` so the server never
 * has to guess which half of the form was filled in, and so a SAML body cannot
 * smuggle a `clientSecret` into an OIDC column.
 */
export const SsoConnectionWriteSchema = z.discriminatedUnion('protocol', [
  z.object({
    protocol: z.literal('oidc'),
    name: nameField,
    isEnabled: z.boolean().default(true),
    jitEnabled: z.boolean().default(true),
    defaultRoleKey: z.string().trim().min(1).max(64).default(SSO_DEFAULT_ROLE_KEY),
    defaultDepartmentScope: SsoDepartmentScopeSchema.default({ all: true, ids: [] }),
    oidc: SsoOidcConfigSchema,
    /**
     * INBOUND ONLY. Omitted on an update, the stored ciphertext is kept; sent
     * as the empty string, it is cleared. There is no way to read it back.
     */
    clientSecret: z.string().max(4096).optional(),
  }),
  z.object({
    protocol: z.literal('saml'),
    name: nameField,
    isEnabled: z.boolean().default(true),
    jitEnabled: z.boolean().default(true),
    defaultRoleKey: z.string().trim().min(1).max(64).default(SSO_DEFAULT_ROLE_KEY),
    defaultDepartmentScope: SsoDepartmentScopeSchema.default({ all: true, ids: [] }),
    saml: SsoSamlConfigSchema,
  }),
]);
export type SsoConnectionWrite = z.infer<typeof SsoConnectionWriteSchema>;

/** The record of the last test-connection attempt. `null` = never tested. */
export const SsoTestResultSchema = z.object({
  outcome: SsoTestOutcomeSchema,
  at: z.string(),
  /** A short operator-readable reason. Never carries a credential. */
  message: z.string().max(2000).nullable(),
});
export type SsoTestResult = z.infer<typeof SsoTestResultSchema>;

/**
 * THE READ SHAPE. No `clientSecret`, by construction.
 */
export const SsoConnectionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  protocol: SsoStoredProtocolSchema,
  isEnabled: z.boolean(),
  // NO `isPrimary`. c36 dropped the column: nothing could set it, nothing read
  // it, and a read shape that carries a field no writer can reach is a promise
  // to the next consumer that this surface cannot keep.
  jitEnabled: z.boolean(),
  defaultRoleKey: z.string(),
  defaultDepartmentScope: z.object({
    all: z.boolean(),
    ids: z.array(z.string().uuid()),
  }),
  oidc: z
    .object({
      issuer: z.string().nullable(),
      clientId: z.string().nullable(),
      scopes: z.array(z.string()),
    })
    .nullable(),
  saml: z
    .object({
      entityId: z.string().nullable(),
      ssoUrl: z.string().nullable(),
      certificate: z.string().nullable(),
      metadataUrl: z.string().nullable(),
    })
    .nullable(),
  /** "is a secret configured" without the secret being answerable. */
  hasClientSecret: z.boolean(),
  lastTest: SsoTestResultSchema.nullable(),
  /** Where the IdP sends the user back. Read-only; the admin copies it. */
  redirectUri: z.string(),
  /** Where a sign-in begins. Read-only; the admin distributes it. */
  signInUrl: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SsoConnection = z.infer<typeof SsoConnectionSchema>;

/**
 * What a user's row says about SSO — the badge's source.
 *
 * `linked` is a fact about `user_identities`, not a column somebody set: a
 * person is SSO-linked exactly when an identity row exists for them, which is
 * the same fact the sign-in path writes.
 */
export const SsoLinkSchema = z.object({
  linked: z.boolean(),
  /** The connection's display name, for the badge. Null when not linked. */
  providerName: z.string().nullable(),
});
export type SsoLink = z.infer<typeof SsoLinkSchema>;
